import AnimateInView from '@atoms/AnimateInView/AnimateInView';
import Button from '@atoms/Button/Button';
import LoadingSpinner from '@atoms/LoadingSpinner/LoadingSpinner';
import Text from '@atoms/Text/Text';
import BreakpointEnum from '@models/enums/BreakpointEnum';
import SizeEnum from '@models/enums/SizeEnum';
import { IColourSwatchCard } from '@models/IColourSwatchCard';
import ColourSwatchCard, {
  ColourSwatchCardBG,
} from '@molecules/ColourSwatchCard/ColourSwatchCard';
import { fadeInUp } from '@utility/config/animations';
import replaceValuesInLabel from '@utility/helpers/replaceValuesInLabel';
import useBreakpoint from '@utility/hooks/useBreakpoint';
import useListingComponentData from '@utility/hooks/useListingComponentData';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';

interface Props {
  readonly applicationAreas: string;
  readonly endpoint: string;
  readonly heading?: string;
  readonly items: IColourSwatchCard[];
  readonly loadingLabel: string;
  readonly paletteId: number;
  readonly resultsLabel: string;
  readonly totalResults: number;
}

const ColourSwatchCardGrid = ({
  applicationAreas,
  endpoint,
  heading,
  items,
  loadingLabel,
  paletteId,
  resultsLabel,
  totalResults,
}: Props) => {
  const breakpoint = useBreakpoint();

  const [pageSize, setPageSize] = useState(20);
  const [listItems, setListItems] = useState<IColourSwatchCard[]>(items); // Start with initial items
  const [currentPage, setCurrentPage] = useState(0);
  const [resultsShown, setResultsShown] = useState(listItems.length);

  const [queryState, setQueryState] = useState({
    page: 0,
    take: pageSize,
    isDefaultData: true,
  });

  const {
    fetchData,
    state: { data, loading },
  } = useListingComponentData<
    { readonly [key: string]: unknown },
    { results: IColourSwatchCard[] } | null
  >(endpoint, { results: items }, false, false);

  const getPageSizeForBreakpoint = (breakpoint: BreakpointEnum) => {
    switch (breakpoint) {
      case BreakpointEnum.Small:
        return 8;
      case BreakpointEnum.Medium:
        return 12;
      default:
        return 20;
    }
  };

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    setQueryState({
      ...queryState,
      page: nextPage,
      take: pageSize,
      isDefaultData: false,
    });
  };

  useEffect(() => {
    const pageSize = getPageSizeForBreakpoint(breakpoint as BreakpointEnum);

    setPageSize(pageSize);
    setListItems(items.slice(0, pageSize));

    setQueryState((prevState) => ({
      ...prevState,
      take: pageSize,
    }));
  }, [breakpoint]);

  useEffect(() => {
    if (queryState.isDefaultData) return;

    fetchData({
      applicationAreas,
      take: queryState.take,
      page: queryState.page,
      paletteIds: paletteId,
      pageUrl: window.location.pathname,
    });
  }, [queryState]);

  useEffect(() => {
    if (queryState.page === 0) {
      setListItems(data?.results ?? []);
    } else {
      setListItems((prevItems) => [
        ...(prevItems ?? []),
        ...(data?.results ?? []),
      ]);
    }
  }, [data]);

  useEffect(() => {
    setResultsShown(listItems.length);
  }, [listItems]);

  return (
    <AnimateInView desktopOffset={0.2} className="tw-container tw-text-black">
      <motion.div variants={fadeInUp}>
        <Text variant="heading-36" className="tw-mb-lg md:tw-mb-2xl">
          {heading}
        </Text>
      </motion.div>
      <div className="tw-flex tw-flex-col tw-items-center tw-gap-sm">
        <div className="tw-grid tw-w-full tw-grid-cols-2 tw-gap-x-xs tw-gap-y-lg md:tw-grid-cols-3 md:tw-gap-x-md md:tw-gap-y-[60px] xl:tw-grid-cols-5">
          {(!loading || queryState.page > 0) &&
            listItems.map((item, index: number) => (
              <motion.div
                variants={fadeInUp}
                key={`${item.colourName}-${index}`}
              >
                <ColourSwatchCard
                  bgColour={ColourSwatchCardBG.White}
                  {...item}
                />
              </motion.div>
            ))}
        </div>

        <motion.div
          variants={fadeInUp}
          className="tw-flex tw-min-h-[112px] tw-items-center tw-justify-center"
        >
          <AnimatePresence>
            {loading ? (
              <LoadingSpinner
                size={SizeEnum.Small}
                loadingLabel={loadingLabel}
              />
            ) : (
              <motion.div
                variants={fadeInUp}
                className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-text-center"
              >
                {resultsShown < totalResults && (
                  <Button
                    onClick={handleLoadMore}
                    variant="grey80"
                    modifier="outline"
                    label={loadingLabel}
                  />
                )}
                <Text variant="body-small">
                  {replaceValuesInLabel(resultsLabel, [
                    resultsShown,
                    totalResults,
                  ])}
                </Text>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </AnimateInView>
  );
};

export default ColourSwatchCardGrid;
